import { useEffect, useRef, useState } from 'react';

import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import PaperBox from 'components/containers/PaperBox';

const { REACT_APP_GOOGLE_MAPS_API_KEY, REACT_APP_GOOGLE_MAPS_API_URL } =
  process.env;

function loadScript(src, position, id) {
  if (!position) {
    return;
  }
  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

// Read-only map that renders a single marker at the given GeoJSON coordinates.
// `coordinates` follows the GeoJSON convention: [longitude, latitude].
function GoogleMapsStaticMap({ coordinates, height }) {
  const refMap = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [ready, setReady] = useState(Boolean(window.google?.maps));

  const hasCoordinates =
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    Number.isFinite(coordinates[0]) &&
    Number.isFinite(coordinates[1]);

  // Ensure the Google Maps script is loaded, then flag when it's ready.
  useEffect(() => {
    if (window.google?.maps) {
      setReady(true);
      return undefined;
    }
    if (typeof window === 'undefined') {
      return undefined;
    }
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `${REACT_APP_GOOGLE_MAPS_API_URL}?key=${REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }
    const intervalId = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(intervalId);
        setReady(true);
      }
    }, 200);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!ready || !hasCoordinates || !refMap.current) {
      return;
    }
    const position = { lat: coordinates[1], lng: coordinates[0] };
    if (!map.current) {
      map.current = new window.google.maps.Map(refMap.current, {
        center: position,
        zoom: 16,
        disableDefaultUI: true,
        zoomControl: true,
      });
    } else {
      map.current.setCenter(position);
    }
    if (!marker.current) {
      marker.current = new window.google.maps.Marker({
        position,
        map: map.current,
      });
    } else {
      marker.current.setPosition(position);
    }
  }, [ready, hasCoordinates, coordinates]);

  if (!hasCoordinates) {
    return (
      <PaperBox
        variant="outlined"
        elevation={0}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          height,
          alignItems: 'center',
          borderRadius: 1,
        }}
      >
        <LocationOffOutlinedIcon fontSize="large" color="action" />
        <Typography variant="caption">
          No hay coordenadas disponibles para esta dirección
        </Typography>
      </PaperBox>
    );
  }

  return (
    <Box>
      <div ref={refMap} style={{ height, borderRadius: 4 }} />
    </Box>
  );
}

GoogleMapsStaticMap.propTypes = {
  coordinates: PropTypes.arrayOf(PropTypes.number),
  height: PropTypes.string,
};

GoogleMapsStaticMap.defaultProps = {
  coordinates: null,
  height: '300px',
};

export default GoogleMapsStaticMap;
